/**
 * React Query hook for fetching audit error summary
 *
 * Returns summary of all errors grouped by priority, category, and type
 * Includes preprocessing stats for tax completeness metrics
 */

import { useQuery } from "@tanstack/react-query";
import { authorizedAxios } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

export interface AuditSummaryResponse {
  success: boolean;
  data: {
    userId: number;
    totalErrors: number;
    byPriority: {
      must_fix: number;
      should_fix: number;
      informational: number;
    };
    byCategory: Record<string, number>;
    byErrorType: Record<string, number>;
    dataQualityScore: number;
    recentActivity: Array<{
      id: number;
      activityType: 'resolved' | 'dismissed' | 'created';
      errorType: string;
      method?: string;
      timestamp: string;
    }>;
    autoFixedByBridge: number;
    preprocessingStats?: {
      total_transactions: number;
      successfully_processed: number;
      excluded_price_errors: number;
      excluded_other_errors: number;
      completeness_percentage: number;
      last_updated: string;
    };
  };
}

/**
 * Hook to fetch audit summary including error counts and preprocessing stats
 */
export const useAuditSummary = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<AuditSummaryResponse>({
    queryKey: ["audit", "summary"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get("/audit/summary");
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 2,
  });
};
